import { IntegrationCard } from '@/components/integrations/IntegrationCard';
import { Button } from '@/components/ui/Button';
import { CircularLoader } from '@/components/ui/CircularLoader';

import { useI18n } from '@/context/I18nProvider';
import { useOnboardingTutorial } from '@/hooks/useOnboardingTutorial';
import { ApiClient } from '@/services/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, PlayCircle, CheckCircle2, X } from 'lucide-react';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type Platform = 'whatsapp' | 'instagram' | null;
type WhatsappType = 'business' | 'personal' | null;

export const SetupWizard = () => {
  const { t } = useI18n();
  const router = useRouter();
  const queryClient = useQueryClient();

  const { dismiss: originalDismiss, completedSteps } = useOnboardingTutorial();

  const dismiss = () => {
    originalDismiss();
    if (router.query.forceSetup === 'true') {
      const newQuery = { ...router.query };
      delete newQuery.forceSetup;
      router.replace({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
    }
  };

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [platform, setPlatform] = useState<Platform>(null);
  const [, setWhatsappType] = useState<WhatsappType>(null);
  const [profileName, setProfileName] = useState('');

  useEffect(() => {
    if (router.query.step) {
      setStep(parseInt(router.query.step as string) as 1 | 2 | 3 | 4);
    } else {
      const savedStep = localStorage.getItem('setup_wizard_step');
      if (savedStep) setStep(parseInt(savedStep) as 1 | 2 | 3 | 4);
    }
  }, [router.query.step]);

  useEffect(() => {
    localStorage.setItem('setup_wizard_step', step.toString());
  }, [step]);

  const mCreate = useMutation({
    mutationFn: () =>
      ApiClient.integration.createPersonalWhatsappProfile({
        ProfileName: profileName.trim(),
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['integration.getWhatsappProfiles'] });
      const profileId = response.data.ProfileID;
      toast.success(t('personalWhatsapp.createSuccess'));
      router.push({
        pathname: `/onboarding/personal-whatsapp/${profileId}`,
        query: router.query,
      });
    },
    onError: () => {
      toast.error(t('personalWhatsapp.createError'));
    },
  });

  useEffect(() => {
    if (completedSteps.includes('connect_integration') && step < 4) {
      setStep(4);
    }
  }, [completedSteps, step]);

  const handleCreatePersonalWA = (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileName.trim()) {
      toast.error(t('personalWhatsapp.nameRequired'));
      return;
    }
    mCreate.mutate();
  };

  return (
    <div className="flex h-full flex-col bg-white animate-in fade-in duration-500">
      <header className="px-4 md:px-6 pt-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-3">
              {step > 1 && step < 4 && (
                <button
                  onClick={() => setStep((prev) => (prev - 1) as 1 | 2 | 3 | 4)}
                  className="p-1 hover:bg-op-bg rounded-full transition-all text-op-ink/60 hover:text-op-ink -ml-1"
                >
                  <ArrowLeft className="size-5" />
                </button>
              )}
              <h1 className="text-lg font-medium leading-tight text-foreground">
                {step === 4 ? t('personalWhatsapp.successTitle') : t('onboardingTutorial.title')}
              </h1>
            </div>
            <p className="text-xs text-muted-foreground">
              {step === 4
                ? t('personalWhatsapp.successDesc')
                : t('onboardingTutorial.steps.connect_integration.description')}
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            <button
              onClick={dismiss}
              className="flex items-center gap-2 text-[10px] font-bold text-gray-400 hover:text-gray-900 transition-colors uppercase tracking-widest"
            >
              <span>Quitter l'onboarding</span>
              <X className="size-3" />
            </button>

            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {Array.from({ length: platform === 'instagram' ? 4 : 6 }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      'h-1 rounded-full transition-all duration-500',
                      i + 1 <= (platform === 'instagram' ? (step === 4 ? 2 : step) : step)
                        ? 'w-4 bg-[#D94E1F]'
                        : 'w-1 bg-gray-200'
                    )}
                  />
                ))}
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                ÉTAPES {platform === 'instagram' ? (step === 4 ? 2 : step) : step} /{' '}
                {platform === 'instagram' ? 4 : 6}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="flex flex-col flex-1 items-center space-y-4 px-4 md:px-6 py-4 overflow-y-auto no-scrollbar">
        {step === 1 && (
          <div className="w-full space-y-6 animate-slide-up">
            <h2 className="text-sm font-medium leading-5 text-text-primary">
              {t('integrations.allApps')}
            </h2>

            <div className="flex flex-wrap gap-3">
              <IntegrationCard
                className="w-full max-w-[285px] h-[133px]"
                title="WhatsApp"
                description={t('integrations.whatsappDesc')}
                icon="WhatsApp"
                actionLabel={t('integrations.connectWhatsapp')}
                onClick={() => {
                  setPlatform('whatsapp');
                  setStep(2);
                }}
              />
              <IntegrationCard
                className="w-full max-w-[285px] h-[133px]"
                title="Instagram"
                description={t('integrations.instagramDesc')}
                icon="IG"
                actionLabel={t('integrations.connectInstagram')}
                onClick={() => {
                  setPlatform('instagram');
                  router.push('/onboarding/instagram?forceSetup=true&platform=instagram');
                }}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="w-full space-y-6 animate-slide-up">
            <h2 className="text-sm font-medium leading-5 text-text-primary">
              {t('personalWhatsapp.connectionType')}
            </h2>

            <div className="flex flex-wrap gap-3">
              <IntegrationCard
                className="w-full max-w-[285px] h-[133px]"
                title={t('integrations.whatsappTitle')}
                description={t('integrations.whatsappDesc')}
                icon="WhatsApp"
                actionLabel={t('getStarted.cta')}
                onClick={() => {
                  setWhatsappType('business');
                  router.push({
                    pathname: '/onboarding/whatsapp-business',
                    query: { ...router.query, platform: 'whatsapp' },
                  });
                }}
              />
              <IntegrationCard
                className="w-full max-w-[285px] h-[133px]"
                title={t('integrations.personalWhatsappTitle')}
                description={t('integrations.personalWhatsappDesc')}
                icon="WhatsApp"
                actionLabel={t('getStarted.cta')}
                badge="Beta"
                onClick={() => {
                  setWhatsappType('personal');
                  router.push({
                    pathname: '/onboarding/personal-whatsapp',
                    query: { ...router.query, platform: 'whatsapp' },
                  });
                }}
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="w-full max-w-xl animate-slide-up space-y-8">
            <div className="space-y-1">
              <h2 className="text-lg font-medium text-op-ink">
                {t('personalWhatsapp.finalizeTitle')}
              </h2>
              <p className="text-sm text-op-ink/60">{t('personalWhatsapp.nameProfileDesc')}</p>
            </div>

            <form onSubmit={handleCreatePersonalWA} className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="profileName"
                  className="text-xs font-bold text-op-ink/60 uppercase tracking-widest ml-1"
                >
                  {t('personalWhatsapp.profileNameLabel')}
                </label>
                <div className="relative">
                  <input
                    id="profileName"
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder={t('personalWhatsapp.profileNamePlaceholder')}
                    className="w-full h-12 rounded-op border border-op-line bg-white px-4 focus:border-op-accent focus:ring-4 focus:ring-op-accent/5 outline-none transition-all text-base font-medium text-op-ink"
                    autoFocus
                  />
                  {profileName && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <CheckCircle2 className="size-5 text-emerald-500" />
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-op border border-amber-100 space-y-3">
                <div className="flex items-center gap-2 text-amber-800 font-bold text-xs uppercase tracking-wider">
                  <PlayCircle className="size-4" />
                  <span>{t('onboardingTutorial.needHelp')}</span>
                </div>
                <p className="text-xs text-amber-700/80 leading-relaxed">
                  {t('personalWhatsapp.helpText')}
                </p>
                <a
                  href="https://docs.orsay.ai/fr/personal-whatsapp/1-connect"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-bold text-amber-900 hover:gap-3 transition-all"
                >
                  {t('getStarted.cta')} <ArrowRight className="size-3" />
                </a>
              </div>

              <Button
                type="submit"
                className="h-12 px-8 rounded-op font-bold text-sm bg-op-accent hover:bg-op-accent/90 transition-all"
                disabled={mCreate.isPending || !profileName.trim()}
              >
                {mCreate.isPending ? (
                  <CircularLoader className="size-5 border-white/30 border-t-white" />
                ) : (
                  t('personalWhatsapp.createAndScanQR')
                )}
              </Button>
            </form>
          </div>
        )}

        {step === 4 && (
          <div className="w-full space-y-8 animate-in zoom-in-95 duration-700">
            <div className="flex flex-wrap gap-3">
              <IntegrationCard
                className="w-full max-w-[285px] h-[133px]"
                title={`1. ${t('onboardingTutorial.steps.create_agent.title')}`}
                description={t('onboardingTutorial.steps.create_agent.description')}
                icon="AgentSidebar"
                actionLabel={t('common.next')}
                onClick={() =>
                  router.push(`/onboarding/agents?forceSetup=true&platform=${platform}`)
                }
              />

              <IntegrationCard
                className="w-full max-w-[285px] h-[133px]"
                title={`2. ${t('onboardingTutorial.steps.create_sequence.title')}`}
                description={t('onboardingTutorial.steps.create_sequence.description')}
                icon="SequencesSidebar"
                actionLabel={t('common.next')}
                isDisabled={!completedSteps.includes('create_agent')}
                onClick={() =>
                  router.push(`/onboarding/create-sequence?forceSetup=true&platform=${platform}`)
                }
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
